/**
 * Camera capture and barcode decoding.
 *
 * The target is Firefox on Android, which has no `BarcodeDetector`, so zxing's wasm build is the
 * only decoder. Frames are cropped to the on-screen reticle before decoding: a retail barcode is
 * wide and short, and a small region of interest is what keeps a decode inside a frame budget on a
 * phone. Only one decode is ever in flight; dropped frames are cheaper than a queue.
 */
import { useCallback, useEffect, useRef, useState } from "react";

type WorkerReply =
	| { type: "ready" }
	| { type: "decoded"; id: number; code: string | null; decodeMs: number; error: string | null };
type Pending = {
	resolve: (value: { code: string | null; decodeMs: number }) => void;
	reject: (reason: Error) => void;
};

const worker = new Worker(new URL("./scanner.worker.ts", import.meta.url), { type: "module" });
const pending = new Map<number, Pending>();
let requestId = 0;
let readyResolve: (() => void) | null = null;
let readyReject: ((reason: Error) => void) | null = null;

/** Started with the application bundle, so camera permission time also hides WASM compilation. */
export const decoderReady = new Promise<void>((resolve, reject) => {
	readyResolve = resolve;
	readyReject = reject;
});

worker.addEventListener("message", (event: MessageEvent<WorkerReply>) => {
	const reply = event.data;
	if (reply.type === "ready") {
		readyResolve?.();
		readyResolve = null;
		readyReject = null;
		return;
	}
	const waiting = pending.get(reply.id);
	if (!waiting) return;
	pending.delete(reply.id);
	if (reply.error) waiting.reject(new Error(reply.error));
	else waiting.resolve({ code: reply.code, decodeMs: reply.decodeMs });
});
worker.addEventListener("error", (event) => {
	const error = new Error(event.message || "barcode decoder worker failed");
	readyReject?.(error);
	readyResolve = null;
	readyReject = null;
	for (const waiting of pending.values()) waiting.reject(error);
	pending.clear();
});

export async function decodeBarcodeImage(
	image: ImageData,
	thorough = false,
): Promise<{ code: string | null; decodeMs: number }> {
	await decoderReady;
	const id = ++requestId;
	const buffer = image.data.buffer as ArrayBuffer;
	const result = new Promise<{ code: string | null; decodeMs: number }>((resolve, reject) => {
		pending.set(id, { resolve, reject });
	});
	worker.postMessage(
		{ type: "decode", id, width: image.width, height: image.height, buffer, thorough },
		[buffer],
	);
	return await result;
}

/** Fraction of the frame the reticle covers; must match `.reticle` in app.css. */
const ROI_WIDTH = 0.9;
const ROI_HEIGHT = 0.26;
const MAX_DECODE_WIDTH = 720;
/** Poll cadence; decode duration is added naturally because the next tick is queued afterwards. */
const FRAME_POLL_MS = 50;
/** Let feedback render and the product leave the reticle before decoding resumes. */
const HIT_PAUSE_MS = 220;

export type ScannerState = "starting" | "running" | "denied" | "unavailable" | "failed";

export type Scanner = {
	video: React.RefObject<HTMLVideoElement | null>;
	state: ScannerState;
	error: string | null;
	torchSupported: boolean;
	torchOn: boolean;
	toggleTorch: () => void;
	retry: () => void;
};

type TorchCapabilities = MediaTrackCapabilities & { torch?: boolean[] | boolean };
type AdvancedConstraint = MediaTrackConstraintSet & { torch?: boolean; focusMode?: string };

let audio: AudioContext | null = null;

/**
 * A supermarket is loud and the phone is in one hand, so a hit has to be felt as well as heard.
 * The tones are deliberately short: this fires once per scanned item.
 */
export function feedback(kind: "hit" | "miss"): void {
	if (typeof navigator.vibrate === "function") navigator.vibrate(kind === "hit" ? 35 : [20, 50, 20]);
	try {
		audio ??= new AudioContext();
		if (audio.state === "suspended") void audio.resume();
		const at = audio.currentTime;
		const osc = audio.createOscillator();
		const gain = audio.createGain();
		osc.type = "sine";
		osc.frequency.value = kind === "hit" ? 1568 : 233;
		const length = kind === "hit" ? 0.07 : 0.22;
		gain.gain.setValueAtTime(0, at);
		gain.gain.linearRampToValueAtTime(0.22, at + 0.008);
		gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
		osc.connect(gain).connect(audio.destination);
		osc.start(at);
		osc.stop(at + length + 0.02);
	} catch {
		// Autoplay policy or no audio device: the vibration already carried the message.
	}
}

export function useScanner(onCode: (code: string) => void, enabled: boolean): Scanner {
	const video = useRef<HTMLVideoElement | null>(null);
	const track = useRef<MediaStreamTrack | null>(null);
	const onCodeRef = useRef(onCode);
	onCodeRef.current = onCode;

	const [state, setState] = useState<ScannerState>("starting");
	const [error, setError] = useState<string | null>(null);
	const [torchSupported, setTorchSupported] = useState(false);
	const [torchOn, setTorchOn] = useState(false);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!enabled) return;

		let cancelled = false;
		let stream: MediaStream | null = null;
		let canvas: HTMLCanvasElement | null = null;
		let context: CanvasRenderingContext2D | null = null;
		let timer: number | null = null;
		let busy = false;
		let pauseUntil = 0;
		let misses = 0;
		let decoderErrors = 0;
		let latchedCode: string | null = null;
		let clearFrames = 0;

		const decode = async (
			element: HTMLVideoElement,
		): Promise<{ code: string | null; decodeMs: number } | null> => {
			const vw = element.videoWidth;
			const vh = element.videoHeight;
			if (vw === 0 || vh === 0) return null;
			const sw = Math.round(vw * ROI_WIDTH);
			const sh = Math.round(vh * ROI_HEIGHT);
			const scale = Math.min(1, MAX_DECODE_WIDTH / sw);
			const dw = Math.max(1, Math.round(sw * scale));
			const dh = Math.max(1, Math.round(sh * scale));
			if (canvas === null) {
				canvas = document.createElement("canvas");
				context = canvas.getContext("2d", { willReadFrequently: true });
			}
			if (context === null) return null;
			// Assigning width/height clears and reallocates the backing store. Camera dimensions are
			// stable, so only do it when the track actually changes resolution.
			if (canvas.width !== dw || canvas.height !== dh) {
				canvas.width = dw;
				canvas.height = dh;
				context.imageSmoothingEnabled = true;
				context.imageSmoothingQuality = "medium";
			}
			context.drawImage(
				element,
				Math.round((vw - sw) / 2),
				Math.round((vh - sh) / 2),
				sw,
				sh,
				0,
				0,
				dw,
				dh,
			);
			return await decodeBarcodeImage(context.getImageData(0, 0, dw, dh), misses > 0 && misses % 6 === 5);
		};

		const queue = (): void => {
			if (cancelled) return;
			// Firefox can expose requestVideoFrameCallback yet pause its callbacks while camera
			// focus/exposure changes. Polling the latest frame is deterministic; `busy` below still
			// guarantees one decode at a time and drops intermediate camera frames.
			timer = window.setTimeout(() => void tick(), FRAME_POLL_MS);
		};

		const tick = async (): Promise<void> => {
			if (cancelled) return;
			const element = video.current;
			const now = performance.now();
			const ready = element !== null && element.readyState >= 2 && !document.hidden;
			if (ready && !busy && now >= pauseUntil) {
				busy = true;
				try {
					const decoded = await decode(element);
					const code = decoded?.code ?? null;
					decoderErrors = 0;
					if (code === null) {
						misses++;
						if (latchedCode !== null && ++clearFrames >= 3) {
							latchedCode = null;
							clearFrames = 0;
						}
					} else if (!cancelled) {
						misses = 0;
						clearFrames = 0;
						pauseUntil = performance.now() + HIT_PAUSE_MS;
						if (latchedCode !== code) {
							latchedCode = code;
							// Feedback is now unblocked by ZXing because decoding happened in a worker.
							feedback("hit");
							onCodeRef.current(code);
						}
					}
				} catch (reason) {
					decoderErrors++;
					if (decoderErrors >= 3) {
						cancelled = true;
						setState("failed");
						setError(reason instanceof Error ? reason.message : "The barcode decoder stopped.");
						if (stream !== null) for (const item of stream.getTracks()) item.stop();
					}
				} finally {
					busy = false;
				}
			}
			queue();
		};

		const start = async (): Promise<void> => {
			if (navigator.mediaDevices === undefined) {
				setState("unavailable");
				setError(
					"This browser exposes no camera. Browsers block the camera API outright on a plain http:// address, so open the app on its https:// host.",
				);
				return;
			}
			setState("starting");
			setError(null);
			try {
				stream = await navigator.mediaDevices.getUserMedia({
					// 720p contains more than enough detail for a 720 px ROI and reaches stable
					// autofocus/exposure sooner than asking the phone to push 1080p frames.
					video: {
						facingMode: { ideal: "environment" },
						width: { ideal: 1280 },
						height: { ideal: 720 },
						frameRate: { ideal: 30, max: 30 },
					},
					audio: false,
				});
			} catch (err) {
				if (cancelled) return;
				const name = err instanceof DOMException ? err.name : "";
				if (name === "NotAllowedError" || name === "SecurityError") {
					setState("denied");
					setError("Camera access was refused. Type barcodes in by hand below, or grant the camera in site settings.");
				} else if (name === "NotFoundError" || name === "OverconstrainedError") {
					setState("unavailable");
					setError("No usable rear camera was found on this device.");
				} else {
					setState("failed");
					setError(err instanceof Error ? err.message : String(err));
				}
				return;
			}
			if (cancelled) {
				for (const t of stream.getTracks()) t.stop();
				return;
			}

			const element = video.current;
			if (element !== null) {
				element.srcObject = stream;
				// A ready, visibly playing camera can still leave this Promise pending on Firefox.
				// The tick already checks readyState, so playback setup must not block registration
				// of the first video-frame callback.
				void element.play().catch(() => {
					// A later frame/user interaction can make playback eligible.
				});
			}

			const videoTrack = stream.getVideoTracks()[0] ?? null;
			track.current = videoTrack;
			if (videoTrack !== null) {
				let capabilities: TorchCapabilities = {};
				try {
					capabilities = videoTrack.getCapabilities();
				} catch {
					// Firefox throws here rather than returning an empty object.
				}
				setTorchSupported(capabilities.torch === true || Array.isArray(capabilities.torch));
				const focus: AdvancedConstraint = { focusMode: "continuous" };
				// Do not put autofocus on the critical path. Some Firefox camera backends leave
				// this Promise pending while focus continues in hardware; waiting here meant the
				// preview was visibly running but the decoder loop had never started.
				void videoTrack.applyConstraints({ advanced: [focus] }).catch(() => {
					// Fixed focus still reads a barcode at normal shopping distance.
				});
			}
			if (cancelled) return;
			setState("running");
			setTorchOn(false);
			queue();
		};

		void start();

		return () => {
			cancelled = true;
			const element = video.current;
			if (timer !== null) window.clearTimeout(timer);
			if (element !== null) element.srcObject = null;
			track.current = null;
			if (stream !== null) for (const t of stream.getTracks()) t.stop();
		};
	}, [enabled, attempt]);

	const toggleTorch = useCallback(() => {
		const videoTrack = track.current;
		if (videoTrack === null) return;
		const next = !torchOn;
		const constraint: AdvancedConstraint = { torch: next };
		videoTrack.applyConstraints({ advanced: [constraint] }).then(
			() => setTorchOn(next),
			() => setTorchSupported(false),
		);
	}, [torchOn]);

	const retry = useCallback(() => setAttempt((n) => n + 1), []);

	return { video, state, error, torchSupported, torchOn, toggleTorch, retry };
}
